import { useCallback, useEffect, useRef, useState } from 'react';

// Minimal Web Speech API typings — lib.dom ships them behind vendor
// prefixes that TS doesn't model well. Keep the shape narrow; we only
// use the fields we touch.
interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}
interface SpeechRecognitionResult {
  0: SpeechRecognitionAlternative;
  isFinal: boolean;
  length: number;
}
interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}
interface SpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}
interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message?: string;
}
interface SpeechRecognitionInstance extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((ev: SpeechRecognitionEvent) => void) | null;
  onerror: ((ev: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}
type SpeechRecognitionCtor = new () => SpeechRecognitionInstance;

function getCtor(): SpeechRecognitionCtor | null {
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export interface UseSpeechOptions {
  /** Language hint, e.g. "en-IN", "hi-IN". Default "en-IN" — Hinglish friendly. */
  lang?: string;
  /** Called with the final transcript on stop() or on silence-timeout. */
  onFinal: (text: string) => void;
  /** Called with live interim text while recognition runs. Optional. */
  onInterim?: (text: string) => void;
}

export interface UseSpeechResult {
  supported: boolean;
  listening: boolean;
  start: () => void;
  stop: () => void;
  error: string | null;
}

// useSpeechRecognition wraps the browser Web Speech API with a React-
// friendly state machine: idle → listening → final → idle. Ends on
// explicit stop() OR when the browser fires 'end' (usually after ~1s
// of silence). The caller gets both interim deltas (for UI feedback)
// and the final transcript (to submit).
export function useSpeechRecognition(opts: UseSpeechOptions): UseSpeechResult {
  const { lang = 'en-IN', onFinal, onInterim } = opts;
  const [listening, setListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recRef = useRef<SpeechRecognitionInstance | null>(null);
  const finalTextRef = useRef<string>('');

  const Ctor = getCtor();
  const supported = Ctor !== null && window.isSecureContext;

  const start = useCallback(() => {
    if (!supported || listening) return;
    setError(null);
    finalTextRef.current = '';

    const rec = new (Ctor as SpeechRecognitionCtor)();
    rec.lang = lang;
    rec.continuous = false;     // stop after one utterance — natural for chat input
    rec.interimResults = true;

    rec.onresult = (ev) => {
      let interim = '';
      let finalText = '';
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const r = ev.results[i];
        const txt = r[0]?.transcript ?? '';
        if (r.isFinal) finalText += txt;
        else interim += txt;
      }
      if (finalText) {
        finalTextRef.current += finalText;
      }
      const liveView = (finalTextRef.current + ' ' + interim).trim();
      onInterim?.(liveView);
    };

    rec.onerror = (ev) => {
      // 'no-speech', 'aborted', 'not-allowed' are common. Map to a
      // short human-readable string.
      const friendly: Record<string, string> = {
        'no-speech': 'No speech detected',
        'audio-capture': 'Microphone unavailable',
        'not-allowed': 'Microphone permission denied',
        'network': 'Network error during recognition',
        'aborted': '',
      };
      const msg = friendly[ev.error] ?? ev.error ?? 'Speech recognition failed';
      if (msg) setError(msg);
    };

    rec.onend = () => {
      setListening(false);
      const final = finalTextRef.current.trim();
      if (final) onFinal(final);
      recRef.current = null;
    };

    try {
      rec.start();
      recRef.current = rec;
      setListening(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start recognition');
      setListening(false);
    }
  }, [supported, listening, Ctor, lang, onFinal, onInterim]);

  const stop = useCallback(() => {
    recRef.current?.stop();
  }, []);

  // Stop on unmount so the mic is released cleanly.
  useEffect(() => {
    return () => {
      recRef.current?.abort();
    };
  }, []);

  return { supported, listening, start, stop, error };
}
